import React from 'react';
import PropTypes from 'prop-types';
import { Select, Button } from '../ui';

/**
 * AppointmentFilters component provides filtering functionality for appointments
 */
const AppointmentFilters = ({
  searchTerm,
  setSearchTerm,
  dateRange,
  setDateRange,
  statusFilter,
  setStatusFilter,
  practitionerFilter,
  setPractitionerFilter,
  practitioners,
  venueFilter,
  setVenueFilter,
  venues,
}) => {
  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  // Generate date for "next X days" options
  const getNextDaysDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  // Handle date range preset selection
  const handleDateRangePreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate, endDate;

    switch (preset) {
      case 'today':
        startDate = today;
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'tomorrow':
        startDate = new Date(today);
        startDate.setDate(today.getDate() + 1);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'this-week':
        startDate = new Date(today);
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust to start week on Monday
        startDate.setDate(today.getDate() - diff);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'next-7-days':
        startDate = today;
        endDate = getNextDaysDate(7);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'next-30-days':
        startDate = today;
        endDate = getNextDaysDate(30);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'all':
        startDate = null;
        endDate = null;
        break;
      default:
        return;
    }

    setDateRange({
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setDateRange({ startDate: null, endDate: null });
    setStatusFilter('all');
    setPractitionerFilter('all');
    setVenueFilter('all');
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      dateRange.startDate ||
      dateRange.endDate ||
      statusFilter !== 'all' ||
      practitionerFilter !== 'all' ||
      venueFilter !== 'all'
    );
  };

  return (
    <div className="space-y-6">
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        {/* <h3 className="text-lg font-medium text-gray-900">筛选条件</h3> */}
        {hasActiveFilters() && (
          <Button variant="ghost" size="small" onClick={clearAllFilters}>
            清除所有筛选
          </Button>
        )}
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Date Range Selector */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            日期范围
          </label>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={dateRange.startDate ? formatDateForInput(dateRange.startDate) : ''}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                className="px-3 py-2 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="开始日期"
              />
              <input
                type="date"
                value={dateRange.endDate ? formatDateForInput(dateRange.endDate) : ''}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                className="px-3 py-2 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="结束日期"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              <Button
                variant="ghost"
                size="small"
                onClick={() => handleDateRangePreset('today')}
              >
                今天
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => handleDateRangePreset('tomorrow')}
              >
                明天
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => handleDateRangePreset('this-week')}
              >
                本周
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => handleDateRangePreset('next-7-days')}
              >
                未来7天
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => handleDateRangePreset('all')}
              >
                全部
              </Button>
            </div>
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <Select
            label="预约状态"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'upcoming', label: '即将到来' },
              { value: 'scheduled', label: '已预约' },
              { value: 'confirmed', label: '已签到' },
              { value: 'completed', label: '已完成' },
              { value: 'cancelled', label: '已取消' },
              { value: 'noshow', label: '未到诊' }
            ]}
          />
        </div>

        {/* Practitioner Filter */}
        <div>
          <Select
            label="调理师"
            value={practitionerFilter}
            onChange={(e) => setPractitionerFilter(e.target.value)}
            options={[
              { value: 'all', label: '全部调理师' },
              ...practitioners.map((practitioner) => ({
                value: practitioner.id,
                label: practitioner.name
              }))
            ]}
          />
        </div>

        {/* Venue Filter */}
        <div>
          <Select
            label="场地"
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            options={[
              { value: 'all', label: '全部场地' },
              ...venues.map((venue) => ({
                value: venue.id,
                label: venue.name
              }))
            ]}
          />
        </div>
      </div>

    </div>
  );
};

AppointmentFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }).isRequired,
  setDateRange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  practitionerFilter: PropTypes.string.isRequired,
  setPractitionerFilter: PropTypes.func.isRequired,
  practitioners: PropTypes.array.isRequired,
  venueFilter: PropTypes.string.isRequired,
  setVenueFilter: PropTypes.func.isRequired,
  venues: PropTypes.array.isRequired
};

export default AppointmentFilters;